import React, { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";

import {
	Alert,
	Button,
	ErrorMessage,
	HookFormInputGroup,
	PaymentMethodsDdl,
	Skeleton,
} from "components";
import { getMappedCopyInfoData } from "components/pages/paymentMethods/paymentMethodForm/getMappedCopyInfoData";
import {
	accountNumberFieldName,
	routingNumberFieldName,
} from "components/pages/paymentMethods/paymentMethodForm/paymentMethodConstants";
import { useLoader } from "hooks";
import { useGetPaymentMethodsListQuery } from "queries";
import { merchantsService } from "services";
import { uiActions } from "store";
import { BillToTypes, PaymentTypes } from "types";
import { required } from "validators/validation";

type PaymentMethodsListProps = {
	applicationId?: string;
	merchantId?: string;
	primaryAgentId?: number | string;
	paymentMethodChosenId?: string;
	billToName?: string;
	paymentTypeName?: string;
	paymentMethodName?: string;
	isApplication?: boolean;
	submitService?: Function;
	entityId?: string | number;
	paymentMethodKey?: string;
	merchantData?: { [key: string]: any };
	isLoading?: boolean;
};

const PaymentMethodsList = ({
	applicationId,
	merchantId,
	primaryAgentId,
	paymentMethodChosenId,
	billToName = "billTo",
	paymentTypeName = "paymentType",
	paymentMethodName = "paymentMethod",
	isApplication = false,
	submitService,
	entityId,
	paymentMethodKey = "paymentMethod",
	merchantData,
	isLoading,
}: PaymentMethodsListProps) => {
	const dispatch = useDispatch();

	const {
		setValue,
		watch,
		register,

		formState: { errors },
	} = useFormContext();

	const billToType = watch(billToName);
	const paymentType = watch(paymentTypeName);
	const paymentMethodId = watch(`${paymentMethodName}Id`);

	const isBillToAgent = Number(billToType) === BillToTypes.agent.value;
	const isBillToMerchant = Number(billToType) === BillToTypes.merchant.value;
	const isPaymentTypeAch = paymentType === PaymentTypes.ach.name;

	const {
		data: paymentMethodsList,
		isFetching: isLoadingPaymentTypes,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
		refetch: refetchPaymentMethods,
	} = useGetPaymentMethodsListQuery({
		applicationId,
		agentId: isBillToAgent ? primaryAgentId : undefined,
		merchantId: isBillToMerchant ? merchantId : undefined,
		paymentMethodChosenId,
		paymentType,
		paymentMethodKey,
	});

	const alertMessage = isPaymentTypeAch
		? "No ACHs were found. Please add the appropriate payment method."
		: "No credit cards were found. Please add the appropriate payment method.";

	useEffect(() => {
		if (!isLoadingPaymentTypes) {
			const isPaymentTypeExist = paymentMethodsList?.pages?.some(page =>
				page?.items?.some(({ id }) => id === paymentMethodId),
			);

			if (!isPaymentTypeExist) {
				setValue(`${paymentMethodName}Id`, "");
			}
		}
	}, [
		isLoadingPaymentTypes,
		paymentMethodId,
		paymentMethodName,
		paymentMethodsList?.pages,
		setValue,
	]);

	const { data: agentData, isLoading: isLoadingAgentData } = useQuery(
		["getAgentSetupValues", primaryAgentId],
		() => merchantsService.getAgent(primaryAgentId),
		{
			enabled: !!primaryAgentId,
			retry: false,
		},
	);

	const copyInfoData = useMemo(() => {
		const copiedData = (() => {
			if (isBillToAgent) {
				if (!agentData) return;
				return {
					[routingNumberFieldName]: agentData?.payment_info?.ach?.aba
						? {
								label: agentData?.payment_info?.ach?.aba,
								value: agentData?.payment_info?.ach?.aba,
						  }
						: {},
					[accountNumberFieldName]: agentData?.payment_info?.ach?.dda,
				};
			}

			return getMappedCopyInfoData(merchantData);
		})();

		return copiedData;
	}, [agentData, merchantData, isBillToAgent]);

	const handleSuccessfulAddingPaymentMethod = useCallback(
		id => {
			refetchPaymentMethods?.().then(({ data }) => {
				setValue(paymentMethodName, id);
				setValue(`${paymentMethodName}Id`, id);
				setValue(
					`${paymentMethodName}Data`,
					data?.pages?.[0]?.items?.find(item => item?.id === id),
				);
			});
		},
		[paymentMethodName, refetchPaymentMethods, setValue],
	);

	const showModalPaymentMethod = () => {
		dispatch(
			uiActions.showModal("ADD_PAYMENT_METHOD", {
				entityId,
				submitService,
				handleSuccessfulAddingPaymentMethod,
				paymentType,
				copyInfoData,
				isLoadingCopyInfoData: isLoadingAgentData,
				isApplication,
				isBillToAgent,
			}),
		);
	};

	const Loader = useLoader(
		isLoading || (isLoadingPaymentTypes && !isFetchingNextPage),
	);

	return (
		<>
			<div className="mb-3">
				<Loader
					skeleton={<Skeleton height={56} />}
					component={
						paymentMethodsList?.pages?.[0] &&
						paymentMethodsList?.pages?.[0]?.count > 0 ? (
							<PaymentMethodsDdl
								paymentMethods={paymentMethodsList}
								fetchNextPage={fetchNextPage}
								isFetchingNextPage={isFetchingNextPage}
								hasNextPage={hasNextPage}
								paymentMethodName={paymentMethodName}
							/>
						) : (
							<Alert variant="notice" message={alertMessage} />
						)
					}
				/>
				<ErrorMessage
					field={`${paymentMethodName}Id`}
					errors={errors}
				/>
			</div>
			<HookFormInputGroup
				register={register}
				name={`${paymentMethodName}Id`}
				validator={{ required }}
				errors={errors}
				hidden
			/>
			<HookFormInputGroup
				register={register}
				name={`${paymentMethodName}ChosenId`}
				errors={errors}
				hidden
			/>
			<HookFormInputGroup
				register={register}
				name={`${paymentMethodName}Data`}
				hidden
			/>

			<Button
				onClick={showModalPaymentMethod}
				value="Add payment method"
				variant="secondary"
				className="px-0 mr-4"
				minWidth="auto"
				disabled={isLoadingPaymentTypes}
			/>
		</>
	);
};

export default PaymentMethodsList;
